// src/new/components/monitor/TriageChat/triageContext.ts
import { FlowRun } from 'new/components/monitor/Runs/components/FlowRunsTable/types';
import {
  ROUTE_FLOWS,
  ROUTE_SOURCES,
  ROUTE_NEXSETS,
  ROUTE_DESTINATIONS,
} from 'new/constants/pathnames';

/** Normalized triage context shared by all entry points (row icon + deep link). */
export interface TriageContext {
  resourceType: string;
  resourceId: string;
  runId: string | null;
  resourceName: string | null;
  status: string | null;
  errorCount: number | null;
  errorSummary: string | null;
  ts: string | null;
  /** The flow to open on the Integrate canvas when arriving via a deep link.
   *  For a flow notification this equals resourceId; for a child resource
   *  (source/nexset/sink) it's the containing flow when the link provides it. */
  flowId?: string | null;
}

export function buildTriageContextFromRow(row: FlowRun): TriageContext {
  return {
    resourceType: 'flows',
    resourceId: String(row.origin_node_id),
    runId: row.run_id != null ? String(row.run_id) : null,
    resourceName: row.name ?? null,
    status: row.runtime_status ?? null,
    errorCount: typeof row.errors === 'number' ? row.errors : null,
    errorSummary: null,
    ts: null,
  };
}

/**
 * Decode the opaque base64url `ctx` blob the sender packs the non-core context
 * into. The blob is a URL-encoded `key=value&…` string, base64url-encoded — so
 * the URL can never be broken by special chars in (e.g.) an error message.
 * Returns the decoded params, or null when absent/malformed (we then fall back
 * to plain query params / minimal context — the link never hard-fails).
 */
function decodeCtxBlob(raw: string | null): URLSearchParams | null {
  if (!raw) return null;
  try {
    let b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = b64.length % 4;
    if (padLen) b64 += '='.repeat(4 - padLen);
    const decoded =
      typeof atob === 'function'
        ? atob(b64)
        : // eslint-disable-next-line no-undef
          Buffer.from(b64, 'base64').toString('binary');
    return new URLSearchParams(decoded);
  } catch {
    return null;
  }
}

/**
 * Parse the triage deep-link query into a context. The unbreakable core
 * (`resource_type` + `resource_id`) is read from plain query params; all other
 * context (`run_id`, `error_message`, `timestamp`, `name`, `flow_id`) is read
 * from the opaque base64url `ctx` blob the sender packs it into. Remains
 * backward-compatible with the original plain param names — canonical
 * (`run_id`/`error_message`/`timestamp`) and the legacy short names
 * (`type`/`id`/`runId`/`err`/`ts`) — so links sent before the `ctx` format still
 * resolve. Unknown params are ignored, so the link stays extensible.
 */
export function buildTriageContextFromParams(params: URLSearchParams): TriageContext | null {
  const pick = (...keys: string[]): string | null => {
    for (const k of keys) {
      const v = params.get(k);
      if (v != null && v !== '') return v;
    }
    return null;
  };
  const resourceId = pick('resource_id', 'id');
  if (!resourceId) return null;

  // Prefer the ctx blob for the non-core fields; fall back to plain params
  // (older links that carried them directly).
  const blob = decodeCtxBlob(params.get('ctx'));
  const fromCtxOrParam = (...keys: string[]): string | null => {
    if (blob) {
      for (const k of keys) {
        const v = blob.get(k);
        if (v != null && v !== '') return v;
      }
    }
    return pick(...keys);
  };

  return {
    resourceType: pick('resource_type', 'type') || 'flows',
    resourceId,
    runId: fromCtxOrParam('run_id', 'runId'),
    resourceName: fromCtxOrParam('name', 'resource_name'),
    status: null,
    errorCount: null,
    errorSummary: fromCtxOrParam('error_message', 'err'),
    ts: fromCtxOrParam('timestamp', 'ts'),
    flowId: fromCtxOrParam('flow_id', 'flowId'),
  };
}

/**
 * Map a triage context to the Integrate-page URL that selects the relevant
 * resource. `/integrate/<listing>/:id` redirects to a search that selects the
 * resource on the canvas. A deep link that carries an explicit `flow_id` always
 * opens that flow; otherwise we open the resource's own listing (defaulting to
 * the flows listing). This is the only place that knows the destination, so the
 * emailed link stays decoupled from routing.
 */
export function integrateUrlForContext(ctx: TriageContext): string {
  if (ctx.flowId) return `${ROUTE_FLOWS}/${encodeURIComponent(ctx.flowId)}`;
  switch (normalizeResourceType(ctx.resourceType)) {
    case 'source':
      return `${ROUTE_SOURCES}/${encodeURIComponent(ctx.resourceId)}`;
    case 'nexset':
      return `${ROUTE_NEXSETS}/${encodeURIComponent(ctx.resourceId)}`;
    case 'sink':
      return `${ROUTE_DESTINATIONS}/${encodeURIComponent(ctx.resourceId)}`;
    case 'flows':
    default:
      return `${ROUTE_FLOWS}/${encodeURIComponent(ctx.resourceId)}`;
  }
}

/**
 * Map any internal/UI resource-type token to the label the triage agent + the
 * context chip use. Accepts the JS resourceTypes constants (`data_sources`,
 * `data_sets`, `data_sinks`, `flows`, …), the notification enum
 * (`SOURCE`/`DATA_SET`/`DATA_SINK`), and the flow-node `data.type` values.
 */
export function normalizeResourceType(raw: string | null | undefined): string {
  const t = (raw ?? '').toString().trim().toLowerCase();
  switch (t) {
    case 'source':
    case 'sources':
    case 'data_source':
    case 'data_sources':
      return 'source';
    case 'nexset':
    case 'dataset':
    case 'data_set':
    case 'data_sets':
      return 'nexset';
    case 'sink':
    case 'destination':
    case 'data_sink':
    case 'data_sinks':
      return 'sink';
    case 'flow':
    case 'flows':
    case 'custom_data_flow':
      return 'flows';
    case 'credential':
    case 'credentials':
      return 'credential';
    case 'data_map':
    case 'data_maps':
    case 'lookup':
      return 'data_map';
    case 'code_container':
    case 'code_containers':
      return 'code_container';
    case 'data_schemas':
    case 'schema_spec':
    case 'data_schema':
      return 'schema_spec';
    default:
      return t || 'flows';
  }
}

/** Build a triage context for any resource (flow node, resource-triage page,
 *  notification, flow listing). `errorSummary` seeds the agent when present. */
export function buildTriageContextFromResource(input: {
  resourceType: string | null | undefined;
  resourceId: string | number | null | undefined;
  resourceName?: string | null;
  errorSummary?: string | null;
}): TriageContext | null {
  if (input.resourceId == null || `${input.resourceId}` === '') return null;
  return {
    resourceType: normalizeResourceType(input.resourceType),
    resourceId: String(input.resourceId),
    runId: null,
    resourceName: input.resourceName ?? null,
    status: null,
    errorCount: null,
    errorSummary: input.errorSummary ?? null,
    ts: null,
  };
}

export function triageContextChipLabel(ctx: TriageContext): string {
  const head = ctx.resourceName ? ctx.resourceName : `${ctx.resourceType} · ID ${ctx.resourceId}`;
  return ctx.resourceName ? `${head} · ID ${ctx.resourceId}` : head;
}

/** Singular, human noun for the resource (resourceType is plural-ish, e.g. "flows"). */
const RESOURCE_NOUN: Record<string, string> = {
  flows: 'flow',
  source: 'source',
  nexset: 'nexset',
  sink: 'sink',
  credential: 'credential',
  data_map: 'lookup',
  code_container: 'code container',
  schema_spec: 'schema',
};

/**
 * The VISIBLE seed prompt shown in the chat as the first user bubble. Framed
 * CONDITIONALLY on the resource's current status (rather than assuming a failure)
 * so a healthy flow doesn't push the agent to hunt for failures. The system-style
 * guardrail (scoping + read-only) is NOT here — it's appended out of view via
 * `buildTriageGuardrail` so the user doesn't see the boilerplate.
 */
export function buildTriagePrompt(ctx: TriageContext): string {
  const noun = RESOURCE_NOUN[normalizeResourceType(ctx.resourceType)] ?? 'resource';
  const displayId = `#${ctx.resourceId}`;
  const header = ctx.resourceName ? `${ctx.resourceName} (${displayId})` : `${noun} ${displayId}`;
  const parts: string[] = [`Triage ${header}`];
  if (ctx.runId) parts.push(`run ${ctx.runId}`);
  if (typeof ctx.errorCount === 'number') parts.push(`${ctx.errorCount} errors`);
  let line = parts.join(', ') + '.';
  if (ctx.errorSummary) line += ` A recent alert reported: ${ctx.errorSummary}.`;
  line +=
    ` Check the current status of this ${noun}.` +
    ' If there are failures or issues, identify what is failing, the likely root cause,' +
    ' and recommend the next steps. If it is healthy, share any relevant observations or' +
    ' suggestions.';
  return line;
}

/**
 * Hidden guardrail appended to the seed prompt sent to the agent but NOT shown in
 * the UI (see `buildTriageSeed`): scopes the agent to this resource and forbids
 * mutations, asking for confirmation before any change. Prompt-level mitigation;
 * server-enforced consent is the larger follow-up (see the consent plan).
 */
export function buildTriageGuardrail(ctx: TriageContext): string {
  const noun = RESOURCE_NOUN[normalizeResourceType(ctx.resourceType)] ?? 'resource';
  return (
    `Stay focused on this ${noun} (ID: ${ctx.resourceId}) — always use ID ${ctx.resourceId} when` +
    ` calling tools to look up this ${noun}; do not look for failures in other flows or resources` +
    ' unless I ask. Before recommending any change, first fetch the current configuration of this' +
    ' resource to confirm the change is not already in effect.' +
    ' Investigate and recommend only — do not make any changes. Do not create,' +
    ' update, activate, pause, or delete this or any flow, source, sink, nexset, credential,' +
    ' or transform. If a fix is warranted, describe the exact change and ask me to confirm' +
    ' first; never apply changes yourself.'
  );
}

/**
 * The full text sent to the agent for the seed turn = visible prompt + hidden
 * guardrail. The caller renders only `buildTriagePrompt(ctx)` in the UI but sends
 * this to the backend, so the guardrail steers the agent without cluttering chat.
 */
export function buildTriageSeed(ctx: TriageContext): { sent: string; display: string } {
  const display = buildTriagePrompt(ctx);
  return { display, sent: `${display} ${buildTriageGuardrail(ctx)}` };
}
